import { Validation } from '../types';

/**
 * Parses a data model path array and returns a string representing its form path while removing the "composite" part.
 *
 * Examples:
 *  - ['simple_string']                                          => 'simple_string'
 *  - ['collection', 1, 'collection_composite', 'simple_string'] => 'collection.1.simple_string'
 *  - ['group', 0, 'group_composite', 'collection', 1, 'collection_composite', 'simple_string']
 *      => 'group.0.collection.1.simple_string'
 *  - ['dimensional', 'de']                                      => 'dimensional'
 *  - ['multi_dimensional', {OBJECT}, 'simple_string']           => 'multi_dimensional.simple_string'
 */
const getFormPathFromDataModelPath = (path: Validation['path']): string => {
    if (path.length === 1) {
        return `${path[0]}`;
    }

    // In case of dimensional, we are only interested in the dimensional and not its key
    if (path.length === 2) {
        return `${path[0]}`;
    }

    // Remove the 'composite' part from the path, ex: [0, 'collection_composite', 'simple_string']
    if (path.length === 3) {
        return `${path[0]}.${path[2]}`;
    }

    // Remove the 'composite' part from the path, ex: ['group', 0, 'group_composite', 'group_information']
    if (path.length === 4) {
        return `${path[0]}.${path[1]}.${path[3]}`;
    }

    // Recursively parse deeply nested paths.
    return getFormPathFromDataModelPath(path.slice(0, 4)) + '.' + getFormPathFromDataModelPath(path.slice(4));
};

const getPathsFromPath = (path: string): string[] => {
    const parts = path.split('.');

    if (parts.length === 1) {
        return [parts[0]];
    }

    parts.pop();

    return [
        path,
        ...getPathsFromPath(parts.join('.')),
    ];
};

/**
 * Parses a data model path array and returns an array of strings representing its path in the form while removing the
 * "composite" part.
 *
 * Examples:
 * - ['simple_string'] => [
 *      'simple_string'
 *   ]
 *
 * - ['group', 0, 'group_composite', 'group_information'] => [
 *      'group.0.group_information',
 *      'group.0',
 *      'group',
 *   ]
 *
 * - ['group', 0, 'group_composite', 'collection', '0', 'collection_composite', 'simple_string'] => [
 *      'group.0.collection.0.simple_string',
 *      'group.0.collection.0',
 *      'group.0.collection',
 *      'group.0',
 *      'group'
 *   ]
 *
 * - ['dimensional', 'de'] => [
 *      'dimensional'
 * ]
 */
const getNestedFormPathsFromDataModelPath = (path: Validation['path']): string[] => {
    const completePathAsString = getFormPathFromDataModelPath(path);
    const paths = getPathsFromPath(completePathAsString);
    return paths;
};

export { getFormPathFromDataModelPath, getPathsFromPath, getNestedFormPathsFromDataModelPath };
